import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { SiteNav } from "@/components/marketing/SiteNav";
import { SiteFooter } from "@/components/marketing/SiteFooter";
import { HeroDashboard } from "@/components/marketing/HeroDashboard";
import { Problem, HowItWorks, IndustriesGrid, ProjectTypes, Integrations, Security } from "@/components/marketing/Sections";
import { Pricing } from "@/components/marketing/Pricing";
import { Faq } from "@/components/marketing/Faq";
import { DemoForm } from "@/components/marketing/DemoForm";
import { useDemoLogin } from "@/pages/Login";

export function useHashScroll() {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView(), 80);
    return () => clearTimeout(t);
  }, [hash, pathname]);
}

export const TryDemoButton = ({ className = "" }) => {
  const [busy, start] = useDemoLogin();
  return (
    <button onClick={start} disabled={busy} data-testid="try-demo-btn" className={`border border-[#334155] text-slate-200 hover:border-amber-500 hover:text-white transition-colors px-6 py-3.5 font-semibold flex items-center justify-center gap-2 ${className}`}>
      {busy && <Loader2 className="w-4 h-4 animate-spin" />}Try the demo
    </button>
  );
};

function Hero() {
  return (
    <section className="relative overflow-hidden grid-bg" data-testid="section-hero">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#F5F7FA]/60 to-[#F5F7FA]" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-20 md:pt-24 md:pb-28 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 fade-up">
          <p className="eyebrow mb-6">Completions and commissioning management</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-slate-50 leading-[0.95]" data-testid="hero-headline">Know your real<br />handover date.</h1>
          <p className="mt-7 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed" data-testid="hero-subheadline">
            Completions and commissioning management linked to your programme, so you can see when your plant, site or system will actually be ready, from real field progress.
          </p>
          <p className="mt-5 text-sm text-slate-400 max-w-2xl border-l-2 border-amber-500 pl-4" data-testid="hero-secondary">
            For oil and gas, petrochemicals, refining, power, nuclear, mining, renewables, grid, water and infrastructure projects.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <a href="#demo" data-testid="hero-request-demo-btn" className="btn-accent px-7 py-3.5 flex items-center justify-center gap-2 group">
              Request a demo <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <TryDemoButton />
          </div>
          <p className="mt-3 text-xs text-slate-500 font-mono">Demo opens two fictional projects in read-only mode.</p>
        </div>
        <div className="lg:col-span-5"><HeroDashboard /></div>
      </div>
    </section>
  );
}

export default function Landing() {
  useHashScroll();
  return (
    <div className="min-h-screen bg-[#080D1A]">
      <SiteNav />
      <main>
        <Hero />
        <Problem />
        <HowItWorks />
        <IndustriesGrid />
        <ProjectTypes />
        <Integrations />
        <Security />
        <Pricing />
        <Faq />
        <DemoForm />
      </main>
      <SiteFooter />
    </div>
  );
}

